/**
 * Firm-defined workflow roles ("Paraplanner", "Onboarding admin"): a named seat
 * held by one person, stored per firm in workflowRoles/{teamId}. Template tasks
 * and completion-email recipients may target a role instead of a person; the
 * engine resolves it to the current holder when a run starts, and changing the
 * holder re-points open tasks (server/lib/workflows/firmRoles.cjs).
 *
 * CommonJS on purpose, like src/utils/workflowRoleKeys.js: both bundles require
 * it. No object spread, no ESM. Distinct from the REPORTING role keys
 * (`assigneeRoleKey`) — never call these `roleKey`.
 *
 * Shapes:
 *   role            { id: 'wr_…', name, email: ''|string, displayName }
 *   task assignee   { type: 'firmRole', firmRoleId, name }   (name = label snapshot)
 *   task doc source { type: 'firmRole', firmRoleId, firmRoleName, manual?: true }
 */

const FIRM_ROLE_ASSIGNEE_TYPE = 'firmRole';
/** Task assignee type: the person who started the run by hand (Sept 2026). */
const STARTER_ASSIGNEE_TYPE = 'starter';
const TASK_ASSIGNEE_TYPES = Object.freeze(['user', 'derived', 'firmRole', STARTER_ASSIGNEE_TYPE]);
const FIRM_ROLE_ID_PREFIX = 'wr_';
const FIRM_ROLE_ID_RE = /^wr_[a-z0-9]{6,32}$/;
const MAX_FIRM_ROLES = 50;
const MAX_FIRM_ROLE_NAME_LENGTH = 60;

function str(value) {
  return value == null ? '' : String(value).trim();
}
function normEmail(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * Whitelist-normalise a roles list. Unknown keys are dropped, emails lower-cased,
 * ids kept when well-formed and minted otherwise (so new rows can arrive without one).
 * @returns {{ roles: Array, errors: Array<{code:string, index:number, message:string}> }}
 */
function normaliseFirmRoles(raw, options) {
  const makeId = options && typeof options.makeId === 'function' ? options.makeId : null;
  const errors = [];
  if (raw == null) return { roles: [], errors: errors };
  if (!Array.isArray(raw)) {
    errors.push({ code: 'firm_roles_not_array', index: -1, message: 'roles must be a list.' });
    return { roles: [], errors: errors };
  }
  if (raw.length > MAX_FIRM_ROLES) {
    errors.push({ code: 'firm_roles_too_many', index: -1, message: 'A firm can have at most ' + MAX_FIRM_ROLES + ' workflow roles.' });
  }
  const seenNames = {};
  const seenIds = {};
  const roles = [];
  raw.slice(0, MAX_FIRM_ROLES).forEach(function (entry, index) {
    const source = entry && typeof entry === 'object' ? entry : {};
    const name = str(source.name);
    if (!name) errors.push({ code: 'firm_role_name_required', index: index, message: 'Role ' + (index + 1) + ' needs a name.' });
    else if (name.length > MAX_FIRM_ROLE_NAME_LENGTH) errors.push({ code: 'firm_role_name_too_long', index: index, message: '"' + name + '" must be ' + MAX_FIRM_ROLE_NAME_LENGTH + ' characters or fewer.' });
    const nameKey = name.toLowerCase();
    if (name && seenNames[nameKey]) errors.push({ code: 'firm_role_name_duplicate', index: index, message: 'There is already a role called "' + name + '".' });
    seenNames[nameKey] = true;
    let id = str(source.id);
    if (id && !FIRM_ROLE_ID_RE.test(id)) {
      errors.push({ code: 'firm_role_id_invalid', index: index, message: 'Role ' + (index + 1) + ' has an invalid id.' });
      id = '';
    }
    if (!id && makeId) id = String(makeId());
    if (id && seenIds[id]) errors.push({ code: 'firm_role_id_duplicate', index: index, message: 'Role ' + (index + 1) + ' repeats an id.' });
    seenIds[id] = true;
    roles.push({ id: id, name: name, email: normEmail(source.email), displayName: str(source.displayName) });
  });
  return { roles: roles, errors: errors };
}

function firmRolesById(roles) {
  const out = {};
  (Array.isArray(roles) ? roles : []).forEach(function (role) { if (role && role.id) out[role.id] = role; });
  return out;
}

function firmRoleById(roles, id) {
  const key = str(id);
  if (!key) return null;
  if (Array.isArray(roles)) {
    for (let i = 0; i < roles.length; i += 1) if (roles[i] && roles[i].id === key) return roles[i];
    return null;
  }
  return roles && roles[key] ? roles[key] : null;
}

function isFirmRoleAssignee(assignee) {
  return Boolean(assignee) && typeof assignee === 'object' && assignee.type === FIRM_ROLE_ASSIGNEE_TYPE;
}

/**
 * Resolve a firm-role assignee to the current holder. Missing role → empty name;
 * empty role → unassigned. Same return shape as resolveWorkflowAssignee.
 */
function resolveFirmRoleAssignee(roles, firmRoleId) {
  const id = str(firmRoleId);
  const role = firmRoleById(roles, id);
  return {
    assignedTo: role ? normEmail(role.email) : '',
    assignedToName: role && role.email ? str(role.displayName) : '',
    assigneeSource: { type: FIRM_ROLE_ASSIGNEE_TYPE, firmRoleId: id, firmRoleName: role ? str(role.name) : '' },
  };
}

/** A run task that was assigned through a role nobody holds. */
function isFirmRoleUnassigned(task) {
  const source = task && task.assigneeSource;
  return Boolean(source) && source.type === FIRM_ROLE_ASSIGNEE_TYPE && !normEmail(task.assignedTo);
}

/**
 * Assignee text for a run task. A task assigned through a firm role nobody holds
 * opens unassigned but keeps the role name, so lists can flag "Role · unassigned".
 * @returns {{ label: string, unassignedRole: boolean }}
 */
function describeWorkflowTaskAssignee(task) {
  const roleName = task && task.assigneeSource && task.assigneeSource.type === FIRM_ROLE_ASSIGNEE_TYPE ? str(task.assigneeSource.firmRoleName) : '';
  const unassignedRole = !(task && task.assignedTo) && Boolean(roleName);
  const label = (task && (task.assignedToName || task.assignedTo)) || (unassignedRole ? roleName + ' · unassigned' : 'Unassigned');
  return { label: label, unassignedRole: unassignedRole };
}

/**
 * What changed between two saved lists.
 * @returns {{ added: Array, removed: Array, changed: Array<{id,name,fromEmail,toEmail}>, renamed: Array<{id,fromName,toName}> }}
 */
function diffFirmRoles(before, after) {
  const prev = firmRolesById(before);
  const next = firmRolesById(after);
  const added = [];
  const removed = [];
  const changed = [];
  const renamed = [];
  Object.keys(next).forEach(function (id) {
    const role = next[id];
    const old = prev[id];
    if (!old) { added.push(role); return; }
    if (normEmail(old.email) !== normEmail(role.email)) changed.push({ id: id, name: role.name, fromEmail: normEmail(old.email), toEmail: normEmail(role.email) });
    if (str(old.name) !== str(role.name)) renamed.push({ id: id, fromName: str(old.name), toName: str(role.name) });
  });
  Object.keys(prev).forEach(function (id) { if (!next[id]) removed.push(prev[id]); });
  return { added: added, removed: removed, changed: changed, renamed: renamed };
}

/**
 * Where a template references any of the given role ids: task assignees and
 * completion-email recipients.
 * @returns {Array<{ taskId, title, firmRoleId, via: 'assignee'|'recipient', stepId?: string }>}
 */
function templateFirmRoleReferences(template, roleIds) {
  const wanted = {};
  (Array.isArray(roleIds) ? roleIds : []).forEach(function (id) { if (str(id)) wanted[str(id)] = true; });
  const anyRole = Object.keys(wanted).length === 0;
  const hit = function (id) { return anyRole ? Boolean(str(id)) : Boolean(wanted[str(id)]); };
  const refs = [];
  const tasks = template && Array.isArray(template.tasks) ? template.tasks : [];
  tasks.forEach(function (task) {
    if (!task) return;
    if (isFirmRoleAssignee(task.assignee) && hit(task.assignee.firmRoleId)) {
      refs.push({ taskId: str(task.id), title: str(task.title), firmRoleId: str(task.assignee.firmRoleId), via: 'assignee' });
    }
    const steps = task.completion && Array.isArray(task.completion.steps) ? task.completion.steps : [];
    steps.forEach(function (step) {
      const recipients = step && Array.isArray(step.recipients) ? step.recipients : [];
      recipients.forEach(function (r) {
        if (r && r.type === FIRM_ROLE_ASSIGNEE_TYPE && hit(r.firmRoleId)) {
          refs.push({ taskId: str(task.id), title: str(task.title), firmRoleId: str(r.firmRoleId), via: 'recipient', stepId: str(step.id) });
        }
      });
    });
  });
  return refs;
}

module.exports = {
  FIRM_ROLE_ASSIGNEE_TYPE,
  STARTER_ASSIGNEE_TYPE,
  TASK_ASSIGNEE_TYPES,
  FIRM_ROLE_ID_PREFIX,
  FIRM_ROLE_ID_RE,
  MAX_FIRM_ROLES,
  MAX_FIRM_ROLE_NAME_LENGTH,
  normaliseFirmRoles,
  firmRolesById,
  firmRoleById,
  isFirmRoleAssignee,
  resolveFirmRoleAssignee,
  isFirmRoleUnassigned,
  describeWorkflowTaskAssignee,
  diffFirmRoles,
  templateFirmRoleReferences,
};
